/* eslint-disable  */
import { axisBottom } from 'd3-axis';
import { scaleOrdinal, scaleTime } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { select, namespaces as d3Namespaces } from 'd3-selection';

export default function d3Timeline() {
  const DISPLAY_TYPES = ['circle', 'rect'];

  let hover = function () {},
    mouseover = function () {},
    mouseout = function () {},
    click = function () {},
    labelFunction = function (label) {
      return label;
    },
    orient = 'bottom',
    width = null,
    height = null,
    rowSeparatorsColor = null,
    backgroundColor = null,
    tickFormat = {
      tickSize: 6,
      tickValues: null,
    },
    colorCycle = scaleOrdinal(schemeCategory10),
    colorPropertyName = null,
    display = 'rect',
    beginning = 0,
    labelMargin = 0,
    labelPadding = 10,
    ending = 0,
    margin = { left: 30, right: 30, top: 30, bottom: 30 },
    stacked = false,
    rotateTicks = false,
    timeIsRelative = false,
    fullLengthBackgrounds = false,
    itemHeight = 20,
    itemMargin = 5,
    showTimeAxis = true,
    showAxisTop = false,
    showTodayLine = false,
    timeAxisTick = false,
    timeAxisTickFormat = { stroke: 'stroke-dasharray', spacing: '4 10' },
    showTodayFormat = {
      marginTop: 25,
      marginBottom: 0,
      width: 1,
      color: colorCycle,
    },
    showBorderLine = false,
    showBorderFormat = {
      marginTop: 25,
      marginBottom: 0,
      width: 1,
      color: colorCycle,
    },
    showAxisHeaderBackground = false,
    showAxisCalendarYear = false,
    axisBgColor = 'white';

  let appendTimeAxis = function (g, xAxis, yPosition) {
    if (showAxisHeaderBackground) {
      appendAxisHeaderBackground(g, 0, 0);
    }

    g.append('g')
      .attr('class', 'axis')
      .attr('transform', 'translate(' + 0 + ',' + yPosition + ')')
      .call(xAxis);
  };

  let appendAxisHeaderBackground = function (g, xAxis, yAxis) {
    g.insert('rect')
      .attr('class', 'row-green-bar')
      .attr('x', xAxis)
      .attr('width', width)
      .attr('y', yAxis)
      .attr('height', itemHeight)
      .attr('fill', axisBgColor);
  };

  let appendTimeAxisTick = function (g, xAxis, maxStack) {
    g.append('g')
      .attr('class', 'axis')
      .attr(
        'transform',
        'translate(' +
          0 +
          ',' +
          (margin.top + (itemHeight + itemMargin) * maxStack) +
          ')',
      )
      .attr(timeAxisTickFormat.stroke, timeAxisTickFormat.spacing)
      .call(
        xAxis
          .tickFormat('')
          .tickSize(
            -(margin.top + (itemHeight + itemMargin) * (maxStack - 1) + 3),
            0,
            0,
          ),
      );
  };

  let appendBackgroundBar = function (yAxisMapping, index, g, data, datum) {
    let greenbarYAxis =
      (itemHeight + itemMargin) * yAxisMapping[index] + margin.top;
    g.selectAll('svg')
      .data(data)
      .enter()
      .insert('rect')
      .attr('class', 'row-green-bar')
      .attr('x', fullLengthBackgrounds ? 0 : margin.left)
      .attr(
        'width',
        fullLengthBackgrounds ? width : width - margin.right - margin.left,
      )
      .attr('y', greenbarYAxis)
      .attr('height', itemHeight)
      .attr(
        'fill',
        backgroundColor instanceof Function
          ? backgroundColor(datum, index)
          : backgroundColor,
      );
  };

  // SVG text has no equivalent of CSS `text-overflow`, so labels that are wider
  // than their gutter have to be measured and cut by hand - otherwise they run
  // over the chart area. Returns true if the label was shortened.
  let truncateText = function (node, label, maxWidth) {
    if (
      !node ||
      maxWidth <= 0 ||
      typeof node.getComputedTextLength !== 'function'
    ) {
      return false;
    }
    // getComputedTextLength() returns 0 when the node isn't rendered (e.g. in
    // tests, or a hidden container), in which case there's nothing to measure.
    if (
      node.getComputedTextLength() === 0 ||
      node.getComputedTextLength() <= maxWidth
    ) {
      return false;
    }

    let truncated = String(label);
    while (truncated.length > 1) {
      truncated = truncated.slice(0, -1);
      node.textContent = truncated.replace(/\s+$/, '') + '…';
      if (node.getComputedTextLength() <= maxWidth) {
        break;
      }
    }
    return true;
  };

  let appendLabel = function (gParent, yAxisMapping, index, hasLabel, datum) {
    let fullItemHeight = itemHeight + itemMargin;
    let rowsDown =
      margin.top +
      5 +
      fullItemHeight / 2 +
      fullItemHeight * (yAxisMapping[index] || 1);
    let label = hasLabel ? labelFunction(datum.label) : datum.id;

    let text = gParent
      .append('text')
      .attr('class', 'timeline-label')
      .attr('transform', 'translate(' + labelMargin + ',' + rowsDown + ')')
      .text(label)
      .on('click', function (d) {
        click(d, index, datum);
      });

    // Keep the label inside the gutter reserved by margin.left, leaving a small
    // gap before the chart area starts.
    let wasTruncated = truncateText(
      text.node(),
      label,
      margin.left - labelMargin - labelPadding,
    );
    if (wasTruncated) {
      // Not rendered by SVG, but shown as a native tooltip on hover.
      text.append('title').text(label);
    }
  };

  function timeline(gParent) {
    // Remove previous chart (if there is one).
    gParent.selectAll('*').remove();

    let g = gParent.append('g');
    let gParentSize = gParent.node().getBoundingClientRect();

    let gParentItem = gParent;

    let yAxisMapping = {},
      maxStack = 1,
      minTime = 0,
      maxTime = 0;

    setWidth();

    // check if the user wants relative time
    // if so, substract the first timestamp from each subsequent timestamps
    if (timeIsRelative) {
      let originTime = 0;
      g.each(function (d) {
        d.forEach(function (datum, index) {
          datum.times.forEach(function (time, j) {
            if (index === 0 && j === 0) {
              originTime = time.start; //Store the timestamp that will serve as origin
              time.start = 0; //Set the origin
              time.end = time.end - originTime; //Store the relative time (millis)
            } else {
              time.start = time.start - originTime;
              time.end = time.end - originTime;
            }
          });
        });
      });
    }

    // check how many stacks we're gonna need
    // do this here so that we can draw the axis before the graph
    if (stacked || ending === 0 || beginning === 0) {
      g.each(function (d) {
        d.forEach(function (datum, index) {
          // create y mapping for stacked graph
          if (stacked && Object.keys(yAxisMapping).indexOf(index) === -1) {
            yAxisMapping[index] = maxStack;
            maxStack++;
          }

          // figure out beginning and ending times if they are unspecified
          datum.times.forEach(function (time) {
            if (beginning === 0) {
              if (
                time.start < minTime ||
                (minTime === 0 && timeIsRelative === false)
              ) {
                minTime = time.start;
              }
            }
            if (ending === 0) {
              if (time.end > maxTime) {
                maxTime = time.end;
              }
            }
          });
        });
      });

      if (ending === 0) {
        ending = maxTime;
      }
      if (beginning === 0) {
        beginning = minTime;
      }
    }

    let scaleFactor =
      (1 / (ending - beginning)) * (width - margin.left - margin.right);

    // draw the axis
    let xScale = scaleTime()
      .domain([beginning, ending])
      .range([margin.left, width - margin.right]);

    let xAxis = axisBottom()
      .scale(xScale)
      .tickFormat(tickFormat.format)
      .tickSize(tickFormat.tickSize);

    if (tickFormat.tickValues != null) {
      xAxis.tickValues(tickFormat.tickValues);
    } else {
      //xAxis.ticks(tickFormat.numTicks || tickFormat.tickTime, tickFormat.tickInterval);
    }

    // draw the chart
    g.each(function (d) {
      d.forEach(function (datum, index) {
        let data = datum.times;
        let hasLabel = typeof datum.label !== 'undefined';

        if (backgroundColor) {
          appendBackgroundBar(yAxisMapping, index, g, data, datum);
        }

        g.selectAll('svg')
          .data(data)
          .enter()
          .append(function (d) {
            return document.createElementNS(
              d3Namespaces.svg,
              'display' in d ? d.display : display,
            );
          })
          .attr('x', getXPos)
          .attr('y', getStackPosition)
          .attr('width', function (d) {
            return (d.end - d.start) * scaleFactor;
          })
          .attr('cy', function (d, i) {
            return getStackPosition(d, i) + itemHeight / 2;
          })
          .attr('cx', getXPos)
          .attr('r', itemHeight / 2)
          .attr('height', itemHeight)
          .style('fill', function (d) {
            let dColorPropName;
            if (d.color) {
              return d.color;
            }
            if (colorPropertyName) {
              dColorPropName = d[colorPropertyName];
              if (dColorPropName) {
                return colorCycle(dColorPropName);
              } else {
                return colorCycle(datum[colorPropertyName]);
              }
            }
            return colorCycle(index);
          })
          .on('mousemove', function (d) {
            hover(d, index, datum);
          })
          .on('mouseover', function (d, i) {
            mouseover(d, i, datum);
          })
          .on('mouseout', function (d, i) {
            mouseout(d, i, datum);
          })
          .on('click', function (d) {
            click(d, index, datum);
          })
          .attr('class', function () {
            return datum.class
              ? 'timelineSeries_' + datum.class
              : 'timelineSeries_' + index;
          })
          .attr('id', function (d, i) {
            // use deprecated id field
            if (datum.id && !d.id) {
              return 'timelineItem_' + datum.id;
            }

            return d.id ? d.id : 'timelineItem_' + index + '_' + i;
          });

        g.selectAll('svg')
          .data(data)
          .enter()
          .append('text')
          .attr('x', getXTextPos)
          .attr('y', getStackTextPosition)
          .text(function (d) {
            return d.label;
          });

        if (rowSeparatorsColor) {
          let lineYAxis =
            itemHeight +
            itemMargin / 2 +
            margin.top +
            (itemHeight + itemMargin) * yAxisMapping[index];
          gParent
            .append('svg:line')
            .attr('class', 'row-separator')
            .attr('x1', Number(margin.left))
            .attr('x2', width - margin.right)
            .attr('y1', lineYAxis)
            .attr('y2', lineYAxis)
            .attr('stroke-width', 1)
            .attr('stroke', rowSeparatorsColor);
        }

        // add the label
        if (hasLabel) {
          appendLabel(gParent, yAxisMapping, index, hasLabel, datum);
        }

        if (typeof datum.icon !== 'undefined') {
          gParent
            .append('image')
            .attr('class', 'timeline-label')
            .attr(
              'transform',
              'translate(' +
                0 +
                ',' +
                (margin.top + (itemHeight + itemMargin) * yAxisMapping[index]) +
                ')',
            )
            .attr('xlink:href', datum.icon)
            .attr('width', margin.left)
            .attr('height', itemHeight);
        }

        function getStackPosition() {
          if (stacked) {
            return margin.top + (itemHeight + itemMargin) * yAxisMapping[index];
          }
          return margin.top;
        }
        function getStackTextPosition() {
          if (stacked) {
            return (
              margin.top +
              (itemHeight + itemMargin) * yAxisMapping[index] +
              itemHeight * 0.75
            );
          }
          return margin.top + itemHeight * 0.75;
        }
      });
    });

    let belowLastItem = margin.top + (itemHeight + itemMargin) * maxStack;
    let aboveFirstItem = margin.top;
    let timeAxisYPosition = showAxisTop ? aboveFirstItem : belowLastItem;
    if (showTimeAxis) {
      appendTimeAxis(g, xAxis, timeAxisYPosition);
    }
    if (timeAxisTick) {
      appendTimeAxisTick(g, xAxis, maxStack);
    }

    if (rotateTicks) {
      g.selectAll('.tick text').attr('transform', function () {
        return `rotate(${rotateTicks})translate(${this.getBBox().width / 2 + 10},${this.getBBox().height / 2})`;
      });
    }

    let gSize = g.node().getBoundingClientRect();
    setHeight();

    if (showBorderLine) {
      g.each(function (d) {
        d.forEach(function (datum) {
          let times = datum.times;
          times.forEach(function (time) {
            appendLine(xScale(time.start), showBorderFormat);
            appendLine(xScale(time.end), showBorderFormat);
          });
        });
      });
    }

    if (showTodayLine) {
      let todayLine = xScale(new Date());
      appendLine(todayLine, showTodayFormat);
    }

    function getXPos(d) {
      return margin.left + (d.start - beginning) * scaleFactor;
    }

    function getXTextPos(d) {
      return margin.left + (d.start - beginning) * scaleFactor + 5;
    }

    function setHeight() {
      if (!height && !gParentItem.attr('height')) {
        if (itemHeight) {
          // set height based off of item height
          height = gSize.height + gSize.top - gParentSize.top;
          // set bounding rectangle height
          select(gParent.node()).attr('height', height);
        } else {
          throw 'height of the timeline is not set';
        }
      } else {
        if (!height) {
          height = gParentItem.attr('height');
        } else {
          gParentItem.attr('height', height);
        }
      }
    }

    function setWidth() {
      if (!width && !gParentSize.width) {
        try {
          width = gParentItem.attr('width');
          if (!width) {
            throw 'width of the timeline is not set. As of Firefox 27, timeline().with(x) needs to be explicitly set in order to render';
          }
        } catch (err) {
          console.log(err);
        }
      } else if (!(width && gParentSize.width)) {
        try {
          width = gParentItem.attr('width');
        } catch (err) {
          console.log(err);
        }
      }
      // if both are set, do nothing
    }

    function appendLine(lineScale, lineFormat) {
      gParent
        .append('svg:line')
        .attr('x1', lineScale)
        .attr('y1', lineFormat.marginTop)
        .attr('x2', lineScale)
        .attr('y2', height - lineFormat.marginBottom)
        .style('stroke', lineFormat.color) //"rgb(6,120,155)")
        .style('stroke-width', lineFormat.width);
    }
  }

  // SETTINGS

  timeline.margin = function (p) {
    if (!arguments.length) {
      return margin;
    }
    margin = p;
    return timeline;
  };

  timeline.orient = function (orientation) {
    if (!arguments.length) {
      return orient;
    }
    orient = orientation;
    return timeline;
  };

  timeline.itemHeight = function (h) {
    if (!arguments.length) {
      return itemHeight;
    }
    itemHeight = h;
    return timeline;
  };

  timeline.itemMargin = function (h) {
    if (!arguments.length) {
      return itemMargin;
    }
    itemMargin = h;
    return timeline;
  };

  timeline.height = function (h) {
    if (!arguments.length) {
      return height;
    }
    height = h;
    return timeline;
  };

  timeline.width = function (w) {
    if (!arguments.length) {
      return width;
    }
    width = w;
    return timeline;
  };

  timeline.display = function (displayType) {
    if (!arguments.length || DISPLAY_TYPES.indexOf(displayType) === -1) {
      return display;
    }
    display = displayType;
    return timeline;
  };

  timeline.labelFormat = function (f) {
    if (!arguments.length) {
      return labelFunction;
    }
    labelFunction = f;
    return timeline;
  };

  timeline.tickFormat = function (format) {
    if (!arguments.length) {
      return tickFormat;
    }
    tickFormat = format;
    return timeline;
  };

  timeline.hover = function (hoverFunc) {
    if (!arguments.length) {
      return hover;
    }
    hover = hoverFunc;
    return timeline;
  };

  timeline.mouseover = function (mouseoverFunc) {
    if (!arguments.length) {
      return mouseover;
    }
    mouseover = mouseoverFunc;
    return timeline;
  };

  timeline.mouseout = function (mouseoutFunc) {
    if (!arguments.length) {
      return mouseout;
    }
    mouseout = mouseoutFunc;
    return timeline;
  };

  timeline.click = function (clickFunc) {
    if (!arguments.length) {
      return click;
    }
    click = clickFunc;
    return timeline;
  };

  timeline.colors = function (colorFormat) {
    if (!arguments.length) {
      return colorCycle;
    }
    colorCycle = colorFormat;
    return timeline;
  };

  timeline.beginning = function (b) {
    if (!arguments.length) {
      return beginning;
    }
    beginning = b;
    return timeline;
  };

  timeline.ending = function (e) {
    if (!arguments.length) {
      return ending;
    }
    ending = e;
    return timeline;
  };

  timeline.labelMargin = function (m) {
    if (!arguments.length) {
      return labelMargin;
    }
    labelMargin = m;
    return timeline;
  };

  // Gap between the end of a label and the start of the chart area. Labels wider
  // than `margin.left - labelMargin - labelPadding` are truncated.
  timeline.labelPadding = function (p) {
    if (!arguments.length) {
      return labelPadding;
    }
    labelPadding = p;
    return timeline;
  };

  timeline.rotateTicks = function (degrees) {
    if (!arguments.length) {
      return rotateTicks;
    }
    rotateTicks = degrees;
    return timeline;
  };

  timeline.stack = function () {
    stacked = !stacked;
    return timeline;
  };

  timeline.relativeTime = function () {
    timeIsRelative = !timeIsRelative;
    return timeline;
  };

  timeline.showBorderLine = function () {
    showBorderLine = !showBorderLine;
    return timeline;
  };

  timeline.showBorderFormat = function (borderFormat) {
    if (!arguments.length) {
      return showBorderFormat;
    }
    showBorderFormat = borderFormat;
    return timeline;
  };

  timeline.showToday = function () {
    showTodayLine = !showTodayLine;
    return timeline;
  };

  timeline.showTodayFormat = function (todayFormat) {
    if (!arguments.length) {
      return showTodayFormat;
    }
    showTodayFormat = todayFormat;
    return timeline;
  };

  timeline.colorProperty = function (colorProp) {
    if (!arguments.length) {
      return colorPropertyName;
    }
    colorPropertyName = colorProp;
    return timeline;
  };

  timeline.rowSeparators = function (color) {
    if (!arguments.length) {
      return rowSeparatorsColor;
    }
    rowSeparatorsColor = color;
    return timeline;
  };

  timeline.background = function (color) {
    if (!arguments.length) {
      return backgroundColor;
    }
    backgroundColor = color;
    return timeline;
  };

  timeline.showTimeAxis = function () {
    showTimeAxis = !showTimeAxis;
    return timeline;
  };

  timeline.showAxisTop = function () {
    showAxisTop = !showAxisTop;
    return timeline;
  };

  timeline.showAxisCalendarYear = function () {
    showAxisCalendarYear = !showAxisCalendarYear;
    return timeline;
  };

  timeline.showTimeAxisTick = function () {
    timeAxisTick = !timeAxisTick;
    return timeline;
  };

  timeline.fullLengthBackgrounds = function () {
    fullLengthBackgrounds = !fullLengthBackgrounds;
    return timeline;
  };

  timeline.showTimeAxisTickFormat = function (format) {
    if (!arguments.length) {
      return timeAxisTickFormat;
    }
    timeAxisTickFormat = format;
    return timeline;
  };

  timeline.showAxisHeaderBackground = function (bgColor) {
    showAxisHeaderBackground = !showAxisHeaderBackground;
    if (bgColor) {
      axisBgColor = bgColor;
    }
    return timeline;
  };

  return timeline;
}
